import {
  DefaultFetchError,
  FetchCreate,
  FetchGet,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchGet,
  useFetchUpdate,
} from "../fetch";

export enum UserNotificationStatus {
  UNREAD = "UNREAD",
  READ = "READ",
}

export enum NotificationType {
  "system" = "System",
  "user" = "User",
  "billing" = "Billing",
  "content" = "Content",
  "analytics" = "Analytics",
}

export enum NotificationStatus {
  sent = "sent",
  draft = "draft",
}

export interface UserNotification {
  id: string;
  status: UserNotificationStatus;
  notification: Notification;
  createdAt: string;
  updatedAt: string;
  notificationType: NotificationType;
}

export interface UserNotificationTableRow extends UserNotification {
  key: string;
}

export interface UserNotificationTableData {
  data: UserNotificationTableRow[];
  total: number;
}

export interface UserNotificationGetParams
  extends PagingParams, Partial<Pick<UserNotification, "status">> {
  orderByColumn?: keyof UserNotification;
}

export interface Notification {
  id: string;
  title: string;
  description: string;
  group: string;
  createdAt: string;
  updatedAt: string;
  notificationType: string;
  status: NotificationStatus;
}

export interface NotificationTableRow extends Notification {
  key: string;
}

export interface NotificationTableData {
  data: NotificationTableRow[];
  total: number;
}

export interface NotificationGetParams
  extends PagingParams, Partial<Omit<Notification, "id">> {
  orderByColumn?: keyof Omit<Notification, "id">;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface NotificationCreateParams extends Omit<
  Notification,
  "id" | "createdAt" | "updatedAt"
> {}

export const useNotificationGet = <DD = PagingDataResponse<Notification>>(
  decorateData?: (data: PagingDataResponse<Notification>) => DD,
): FetchGet<
  PagingDataResponse<Notification>,
  NotificationGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("notifications", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
    config: {
      params: {
        orderBy: "ASC",
        orderByColumn: "createdAt",
      },
    },
  });

export const useNotificationTableGet = () =>
  useNotificationGet<NotificationTableData>(
    ({
      data,
      meta,
    }: PagingDataResponse<Notification>): NotificationTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export const useNotificationCreate = (): FetchCreate<
  NotificationCreateParams,
  DefaultFetchError,
  NotificationCreateParams
> => useFetchCreate("notifications");

export const useNotificationUpdate = (
  id?: string,
): FetchUpdate<
  NotificationCreateParams,
  DefaultFetchError,
  NotificationCreateParams
> => useFetchUpdate("notifications", id);

export const useUserNotificationsGet = <
  DD = PagingDataResponse<UserNotification>,
>(
  decorateData?: (data: PagingDataResponse<UserNotification>) => DD,
): FetchGet<
  PagingDataResponse<UserNotification>,
  UserNotificationGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("user-notifications/me", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
    // config: {
    //   params: {
    //     orderBy: "DESC",
    //     orderByColumn: "createdAt",
    //   },
    // },
  });

export const useUserNotificationTableGet = () =>
  useUserNotificationsGet<UserNotificationTableData>(
    ({
      data,
      meta,
    }: PagingDataResponse<UserNotification>): UserNotificationTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export interface UserNotificationUpdateParams {
  status: UserNotificationStatus;
}

export const useNotificationMarkRead = (
  id?: string,
): FetchUpdate<
  UserNotificationUpdateParams,
  DefaultFetchError,
  UserNotification
> => useFetchUpdate(`user-notifications${id ? `${id}/read` : ""}`);

export const useNotificationMakUnRead = (
  id?: string,
): FetchUpdate<
  UserNotificationUpdateParams,
  DefaultFetchError,
  UserNotification
> => useFetchUpdate(`user-notifications${id ? `${id}/unread` : ""}`);

export const useNotificationMarkReadAll = (): FetchCreate<
  undefined,
  DefaultFetchError,
  undefined
> => useFetchCreate("user-notifications/read-all");

export const useNotificationMarkUnReadAll = (): FetchCreate<
  undefined,
  DefaultFetchError,
  undefined
> => useFetchCreate("user-notifications/unread-all");
